import { AType, EType, summon, tagged } from "@silversea-ssc/functional-support"
import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import { AirClass, RequestAirFare } from "./air-fare"

export const GatewayParamCountryDefault = summon((F) =>
    F.interface(
        {
            type: F.stringLiteral("countryDefault"),
        },
        "GatewayParamCountryDefault",
    ),
)
export type GatewayParamCountryDefault = AType<typeof GatewayParamCountryDefault>
export type GatewayParamCountryDefaultEncoded = EType<typeof GatewayParamCountryDefault>

export const GatewayParamUserSelected = summon((F) =>
    F.interface(
        {
            type: F.stringLiteral("userSelected"),
            gateway: F.string(),
        },
        "GatewayParamUserSelected",
    ),
)
export type GatewayParamUserSelected = AType<typeof GatewayParamUserSelected>
export type GatewayParamUserSelectedEncoded = EType<typeof GatewayParamUserSelected>

const _GatewayParam = tagged("type")({
    countryDefault: GatewayParamCountryDefault,
    userSelected: GatewayParamUserSelected,
})
export type GatewayParam = AType<typeof _GatewayParam>
export type GatewayParamEncoded = EType<typeof _GatewayParam>

const countryDefault = _GatewayParam.of.countryDefault({})

export const GatewayParam = /* @__PURE__ */ Object.assign(_GatewayParam, {
    maybeGateway: _GatewayParam.match({
        countryDefault: (): O.Option<string> => O.none,
        userSelected: (x) => O.some(x.gateway),
    }),
    countryDefault,
    userSelected: (gateway: string) => GatewayParam.as.userSelected({ gateway }),
})

export const AirParamsNotAvailable = summon((F) =>
    F.interface(
        {
            type: F.stringLiteral("notAvailable"),
        },
        "AirParamsNotAvailable",
    ),
)
export type AirParamsNotAvailable = AType<typeof AirParamsNotAvailable>
export type AirParamsNotAvailableEncoded = EType<typeof AirParamsNotAvailable>

export const AirParamsCruiseOnly = summon((F) =>
    F.interface(
        {
            type: F.stringLiteral("cruiseOnly"),
        },
        "AirParamsCruiseOnly",
    ),
)
export type AirParamsCruiseOnly = AType<typeof AirParamsCruiseOnly>
export type AirParamsCruiseOnlyEncoded = EType<typeof AirParamsCruiseOnly>

export const AirParamsFlight = summon((F) =>
    F.interface(
        {
            type: F.stringLiteral("flight"),
            airClass: AirClass(F),
            gateway: GatewayParam(F),
        },
        "AirParamsFlight",
    ),
)
export type AirParamsFlight = AType<typeof AirParamsFlight>
export type AirParamsFlightEncoded = EType<typeof AirParamsFlight>

const _AirParams = tagged("type")({
    notAvailable: AirParamsNotAvailable,
    cruiseOnly: AirParamsCruiseOnly,
    flight: AirParamsFlight,
})
export type AirParams = AType<typeof _AirParams>
export type AirParamsEncoded = EType<typeof _AirParams>

const gateway = AirParamsFlight.lensFromProp("gateway")
const airClass = AirParamsFlight.lensFromProp("airClass")
const notAvailable = _AirParams.of.notAvailable({})
const cruiseOnly = _AirParams.of.cruiseOnly({})

export const AirParams = /* @__PURE__ */ Object.assign(_AirParams, {
    asRequestAirFare: _AirParams.match(
        {
            notAvailable: F.constant<RequestAirFare>("NotAvailable"),
            cruiseOnly: F.constant<RequestAirFare>("CruiseOnly"),
        },
        (x) => x.airClass,
    ),
    maybeGateway: _AirParams.match(
        {
            flight: F.flow(gateway.get, GatewayParam.maybeGateway),
        },
        (): O.Option<string> => O.none,
    ),
    asPackageType: _AirParams.match(
        {
            flight: () => "AirSea" as const,
        },
        () => "CruiseOnly" as const,
    ),
    gatewayOrUndefined: (airParams: AirParams): string | undefined =>
        F.pipe(airParams, AirParams.maybeGateway, O.getOrElseW(F.constUndefined)),
    notAvailable,
    cruiseOnly,
    updateGateway: (g: GatewayParam) =>
        _AirParams.match({
            notAvailable: (x) => x,
            cruiseOnly: (x) => x,
            flight: gateway.set(g),
        }),
    updateAirClass: (a: AirClass) =>
        _AirParams.match({
            notAvailable: (x) => x,
            cruiseOnly: (x) => x,
            flight: airClass.set(a),
        }),
})
